import { Effect } from 'effect';

import { PdfTextLayer } from '../parser-host';
import { makeParserError, type ParserDefinition } from '../parser-layer';
import { routeParser } from '../combinators';
import { textLayerParser } from './textlayer';

export interface HybridTextOcrOptions {
  readonly minimumTextCharacters?: number;
}

/**
 * Reference hybrid: use the embedded text layer when it is meaningful,
 * otherwise route the page to the injected OCR definition.
 */
export function hybridTextOcrParser<R>(
  ocr: ParserDefinition<R>,
  options: HybridTextOcrOptions = {},
) {
  const minimumTextCharacters = options.minimumTextCharacters ?? 20;
  return routeParser({
    parsers: { textlayer: textLayerParser, ocr },
    route: Effect.fn('okrapdf/parser-sdk.hybridTextOcr.route')(function* (input) {
      const textLayer = yield* PdfTextLayer;
      const page = yield* textLayer.extractPage(input.document, input.page).pipe(
        Effect.mapError((cause) =>
          makeParserError(
            {
              parserId: `${textLayerParser.spec.id}+${ocr.spec.id}`,
              page: input.page,
              code: cause.code,
              retryable: cause.retryable,
              message: 'failed to inspect the PDF text layer for hybrid routing',
            },
            cause,
          ),
        ),
      );
      const characters = page.items.reduce((total, item) => total + item.str.trim().length, 0);
      return characters >= minimumTextCharacters ? ('textlayer' as const) : ('ocr' as const);
    }),
  });
}
